import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { MainContext } from '../../../../../../context/MainContext';
import { createScannedDocument } from '../../../../../../services/api/scannedDocuments';
import {
  getProjectTemplateById,
  updateProjectTemplate,
} from '../../../../../../services/api/projectTemplates';
import ScannedDocumentsLayout from '../../Commons/ScannedDocumentsLayout';
import SuccessPage from '../../Commons/ScannedDocumentsLayout/components/SuccessPage/index';
import { useIntl } from 'react-intl';
import { getLaboratory } from '../../../../../../services/api/laboratory';
import laboratoryFieldGroups from '../Commons/laboratoryFieldGroups';
import LayoutHeader from '../Commons/LayoutHeader';
import { checkDiffTemplate } from '../../Commons/ScannedDocumentsLayout/utils.index';

const CreateLaboratoryScannedDoc = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [success, setSuccess] = useState(false);
  const [diffTemplate, setDiffTemplate] = useState(false);
  const { setLoading, setError } = useContext(MainContext);
  const { project } = useOutletContext();
  const { templateId, model } = useParams();
  const [initialData, setInitialData] = useState({
    form: {
      templateName: '',
      supplier_id: null,
    },
    regions: [],
  });
  const [template, setTemplate] = useState(null);
  const validableFields = useMemo(
    () =>
      laboratoryFieldGroups.mandatory.filter((field) => field !== 'limit_time'),
    [],
  );

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);

        const loadedTemplate = (
          await getProjectTemplateById({ id: templateId })
        ).data;
        const loadedLab = (await getLaboratory(loadedTemplate.labId)).data;

        setTemplate(loadedTemplate);
        setInitialData({
          form: {
            templateName: loadedTemplate.name,
            supplier_id: loadedTemplate.supplier,
            phone_number: loadedLab.phone_number,
            email: loadedLab.email,
          },
          regions: loadedTemplate.regions,
        });
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError('Error loading initial data');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [model, setError, setLoading, templateId]);

  const createLaboratoryScannedDocument = async (
    regions,
    formState,
    fileName,
  ) => {
    try {
      setLoading(true);

      const compressiveStrength = formState.compressive_strength;
      const defaultCompressiveStrength = {};
      const extraCompressiveStrength = {};

      compressiveStrength.forEach((item) => {
        const day = Number(item.day);
        if (day === 3 || day === 7 || day === 28 || day === 60) {
          Object.entries(item).forEach(([key, value]) => {
            if (key !== 'day') {
              defaultCompressiveStrength[
                `compressive_strength_${day}_${key.split('value')[1]}`
              ] = parseFloat(value);
            }
          });
        } else {
          Object.entries(item).forEach(([key, value]) => {
            if (key !== 'day') {
              extraCompressiveStrength[
                `compressive_strength_${day}_${key.split('value')[1]}`
              ] = parseFloat(value);
            }
          });
        }
      });

      const scannedDocument = await createScannedDocument(
        {
          fileName,
          template: templateId,
          pid: project.id_project,
          docket_number: formState.docket_number,
          regions,
          laboratory_note_data: {
            ...formState,
            ...defaultCompressiveStrength,
            strength_extra: extraCompressiveStrength,
          },
        },
        'laboratory',
      );

      if (scannedDocument.data) {
        setSuccess(true);
        if (checkDiffTemplate(regions, template.regions)) {
          setDiffTemplate(regions);
        }
      }
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <SuccessPage
        title={intl.formatMessage({
          id: 'pages.ocr.scannedDocuments.laboratory.create.success.title',
        })}
        subtitle={intl.formatMessage({
          id: 'pages.ocr.scannedDocuments.laboratory.create.success.subtitle',
        })}
        checkDiffTemplate={Boolean(diffTemplate)}
        onBackToOcr={() =>
          navigate(`/app/project/${project.id_project}/quality/ocr`)
        }
        onSaveTemplate={async () => {
          const newTemplate = { ...template, regions: diffTemplate };
          setLoading(true);
          await updateProjectTemplate(newTemplate);
          setLoading(false);
          navigate(`../../ocr`, {
            replace: true,
          });
        }}
      />
    );
  }
  return (
    <>
      <ScannedDocumentsLayout
        model="laboratory"
        initialData={initialData}
        fieldGroups={laboratoryFieldGroups}
        validableFields={validableFields}
        onFinish={createLaboratoryScannedDocument}
        onBack={() =>
          navigate(`/app/project/${project.id_project}/quality/ocr`)
        }
        header={
          <LayoutHeader
            template={initialData?.form?.templateName}
            supplier={initialData?.form?.supplier_id}
            phone={initialData?.form?.phone_number}
            email={initialData?.form?.email}
          />
        }
      />
    </>
  );
};

export default CreateLaboratoryScannedDoc;
